/**
 * 규칙 트리를 비전문가가 읽을 수 있는 문장으로 바꾸는 **표시 전용** 유틸.
 *
 * ⛔ 여기서 규칙을 평가하거나 readiness 를 판정하지 않는다. 판정은 전부 백엔드
 *    (evaluate_project_readiness / match-campaign) 단일 소스에 있고, 이 파일은
 *    이미 받은 응답을 사람 말로 렌더링만 한다.
 *
 * keyMap: { [key_name]: { label, valueLabels: { [actual_value]: 선택지_라벨 } } }
 *   - 신청 질문(input_keys)에서 만들어 넘긴다. key_name 이 keyMap 에 없으면
 *     "규칙은 참조하지만 신청서에 없는 질문" 이므로 문장에서 경고로 표시한다.
 */

const OPERATOR_TEXT = {
    '==': '같음',
    '!=': '같지 않음',
    '<': '미만',
    '<=': '이하',
    '>': '초과',
    '>=': '이상',
    'IN': '다음 중 하나',
};

// 규칙 트리를 문장화할 최대 깊이. 이보다 깊게 중첩된 규칙은 완전 문장화하지 않고
// "복잡한 규칙 (고급에서 편집)" 으로 표기한다 (판정에는 영향 없음, 표시만).
const MAX_DEPTH = 2;

export const operatorText = (op) => OPERATOR_TEXT[op] || op;

/**
 * 조건값을 사람 말로. IN 이면 배열 각 원소를 선택지 라벨로, 그 외는 통째로 라벨 해석.
 */
const valueText = (condition, entry) => {
    const raw = condition.value;
    const resolve = (v) => {
        if (entry && entry.valueLabels && Object.prototype.hasOwnProperty.call(entry.valueLabels, v)) {
            return entry.valueLabels[v];
        }
        return v;
    };
    if (condition.operator === 'IN' && Array.isArray(raw)) {
        return raw.map(resolve).join(', ');
    }
    if (Array.isArray(raw)) {
        // ==/!= 로 배열 전체를 비교하는 드문 경우: 원본을 그대로 보여준다.
        return JSON.stringify(raw);
    }
    if (raw !== null && typeof raw === 'object') {
        return JSON.stringify(raw);
    }
    return String(resolve(raw));
};

/**
 * 조건 1개 → { text, missing }.
 *
 * ⛔ missing 판정은 프론트 재계산이 아니라 **백엔드 readiness.missing_keys** 로만 한다.
 *    keyMap 은 오직 라벨 변환에 쓴다 (single-source 유지). missingSet 은 Set<key_name>.
 */
export const conditionText = (condition, keyMap = {}, missingSet = null) => {
    const entry = keyMap[condition.key];
    const label = entry ? entry.label : condition.key;
    const text = `${label} ${operatorText(condition.operator)} ${valueText(condition, entry)}`;
    const missing = !!(missingSet && missingSet.has(condition.key));
    return {text, missing, key: condition.key};
};

const connectorText = (operator) => (operator === 'OR' ? '또는' : '그리고');

const toSet = (missingKeys) => (missingKeys instanceof Set ? missingKeys : new Set(missingKeys || []));

/**
 * 규칙 그룹(트리) → { parts:[{text, missing}], connector, complex }
 * parts 는 UI 가 목록으로 렌더링하기 좋게 조건별로 쪼갠 것.
 * missingKeys(백엔드 readiness.missing_keys)에 든 key 만 missing=true 로 표시한다.
 */
export const ruleGroupToParts = (group, keyMap = {}, missingKeys = null, depth = 0) => {
    const missingSet = toSet(missingKeys);
    const result = {parts: [], connector: connectorText(group && group.operator), complex: false};
    if (!group) return result;

    (group.conditions || []).forEach((cond) => {
        result.parts.push(conditionText(cond, keyMap, missingSet));
    });

    (group.child_groups || []).forEach((child) => {
        if (depth + 1 >= MAX_DEPTH) {
            // 너무 깊은 중첩은 문장화하지 않는다 (고급 화면에서 편집하도록 안내).
            result.complex = true;
            return;
        }
        const nested = ruleGroupToParts(child, keyMap, missingSet, depth + 1);
        const inner = nested.parts.map((p) => p.text).join(` ${nested.connector} `);
        result.parts.push({text: `( ${inner} )`, missing: nested.parts.some((p) => p.missing)});
        if (nested.complex) result.complex = true;
    });

    return result;
};

/**
 * 규칙 그룹 → 한 줄 문장 (요약 표시용).
 */
export const ruleGroupToSentence = (group, keyMap = {}) => {
    const {parts, connector, complex} = ruleGroupToParts(group, keyMap);
    if (parts.length === 0) return complex ? '복잡한 규칙 (고급에서 편집)' : '조건 없음 (모든 신청자)';
    const body = parts.map((p) => p.text).join(` ${connector} `);
    return complex ? `${body} …(복잡한 규칙, 고급에서 편집)` : body;
};

/**
 * input_keys 배열 → keyMap. label 은 ko 우선, 없으면 key_name.
 */
export const buildKeyMap = (inputKeys = []) => {
    const map = {};
    (inputKeys || []).forEach((k) => {
        const valueLabels = {};
        (k.allowed_values || []).forEach((av) => {
            const lt = av.label_texts || {};
            valueLabels[av.actual_value] = lt.ko || av.actual_value;
        });
        const lt = k.label_texts || {};
        map[k.key_name] = {label: lt.ko || k.key_name, valueLabels};
    });
    return map;
};

const VALUE_TYPE_TEXT = {int: '숫자', str: '글자', list: '선택지'};
export const valueTypeText = (t) => VALUE_TYPE_TEXT[t] || t;
